import React, { useState } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { HiSparkles, HiExternalLink, HiShieldCheck } from './Icons';
import './AmexPointsCalculator.css';

interface AmexPointsCalculatorProps {
  referralLink?: string;
  initialPoints?: number;
}

const PRESETS = [
  { label: 'Gold Card', points: 50000 },
  { label: 'Platinum Aktion', points: 85000, featured: true },
  { label: 'Top Deal', points: 100000 },
  { label: 'Business Card', points: 150000 }
];

export const AmexPointsCalculator: React.FC<AmexPointsCalculatorProps> = ({
  referralLink = 'https://americanexpress.com/de-de/referral/platinum?ref=yUHOUZXrYs&XL=MIANS',
  initialPoints = 85000
}) => {
  const [points, setPoints] = useState<number>(initialPoints);

  // Conversion calculations
  const flightValueMin = Math.round(points * 0.012);
  const flightValueMax = Math.round(points * 0.015);
  const travelValue = Math.round(points * 0.006);
  const cashValue = Math.round(points * 0.004);

  const formatEuro = (val: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
  };

  const formatPoints = (val: number) => {
    return new Intl.NumberFormat('de-DE').format(val);
  };

  return (
    <div className="amex-calculator-wrapper my-4">
      <Card className="amex-calculator-card">
        <div className="amex-calc-header">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div className="d-flex align-items-center gap-2">
              <span className="amex-logo-badge">Amex Points</span>
              <Badge bg="warning" text="dark" className="fw-bold">
                <HiSparkles className="me-1" /> Werte-Rechner
              </Badge>
            </div>
            <span className="amex-badge-sub">Membership Rewards® Wert</span>
          </div>
          <h3 className="calc-title">Was sind deine American Express Punkte wert?</h3>
          <p className="calc-subtitle">
            Punkte einlösen und maximalen Gegenwert herausholen. Wähle deinen Punktestand und sieh die geschätzten Gegenwerte in Euro.
          </p>
        </div>

        {/* Slider & Presets */}
        <div className="calc-slider-box mb-4">
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <span className="slider-label">Gewählte Punktzahl:</span>
            <span className="points-display-value">
              {formatPoints(points)} <span className="points-unit">Punkte</span>
            </span>
          </div>

          <input
            type="range"
            min="10000"
            max="150000"
            step="5000"
            value={points}
            onChange={(e) => setPoints(Number(e.target.value))}
            className="form-range amex-points-slider"
            aria-label="Amex Punkte Slider"
          />

          <div className="presets-row d-flex flex-wrap gap-2 mt-3">
            <span className="presets-title me-1">Schnellauswahl:</span>
            {PRESETS.map((preset) => (
              <button
                key={preset.points}
                type="button"
                className={`preset-pill ${points === preset.points ? 'active' : ''}`}
                onClick={() => setPoints(preset.points)}
              >
                {preset.label} ({formatPoints(preset.points)})
              </button>
            ))}
          </div>
        </div>

        {/* Breakdown Tiers */}
        <Row className="g-3 mb-4">
          {/* Best Value: Airline Transfer */}
          <Col md={4} sm={12}>
            <div className="value-tier-card tier-highlight">
              <div className="tier-badge-top">Bester Gegenwert</div>
              <div className="tier-icon">✈️</div>
              <h4 className="tier-title">Flüge & Upgrades</h4>
              <p className="tier-desc">
                Transfer zu Vielfliegerprogrammen (Emirates, Singapore Airlines, British Airways, Qatar)
              </p>
              <div className="tier-amount text-gradient">
                {formatEuro(flightValueMin)} – {formatEuro(flightValueMax)}
              </div>
              <div className="tier-rate">ca. 1,2 – 1,5 Cent / Punkt</div>
            </div>
          </Col>

          {/* Medium Value: Travel Portal */}
          <Col md={4} sm={12}>
            <div className="value-tier-card">
              <div className="tier-icon">🏨</div>
              <h4 className="tier-title">Reisen & Hotels</h4>
              <p className="tier-desc">
                Direkte Buchung im Amex Reiseportal oder Punktetransfer zu Hilton & Marriott
              </p>
              <div className="tier-amount">
                {formatEuro(travelValue)}
              </div>
              <div className="tier-rate">ca. 0,6 Cent / Punkt</div>
            </div>
          </Col>

          {/* Base Value: Cash / Statement */}
          <Col md={4} sm={12}>
            <div className="value-tier-card">
              <div className="tier-icon">💳</div>
              <h4 className="tier-title">Kartenguthaben & Amazon</h4>
              <p className="tier-desc">
                Kartentransaktionen nachträglich mit Punkten begleichen oder bei Amazon einlösen
              </p>
              <div className="tier-amount">
                {formatEuro(cashValue)}
              </div>
              <div className="tier-rate">ca. 0,4 Cent / Punkt</div>
            </div>
          </Col>
        </Row>

        {/* Bottom Action Bar */}
        <div className="calc-action-footer d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="calc-info-note">
            <HiShieldCheck className="text-success me-1" />
            <span>Offizielle American Express Aktion • Geprüfter Einladungslink</span>
          </div>
          <a
            href={referralLink}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary amex-cta-btn"
          >
            Jetzt {formatPoints(points)} Punkte Bonus sichern <HiExternalLink className="ms-1" />
          </a>
        </div>
      </Card>
    </div>
  );
};

export default AmexPointsCalculator;
